import React, { useEffect, useState, useRef } from "react";
import styled, { css } from "styled-components";
import fonts from "../design/typography";
import colors from "../design/colors";
import { ChevronDown } from "./icons/chevron";
import { ChevronUp } from "./icons/chevron";
import { useClickOutsideCloseModal } from "@/hooks/useClickOutsideCloseElement";

/* **********************************
 * TYPES
 * 타입 정의
 ********************************** */

export interface IOption {
  label: string;
  onSelect: () => void;
}

interface SelectBoxProps {
  width?: string;
  height?: string;
  options: IOption[];
  defaultOptionLabel?: string;
  selectedOptionLabel?: string;
  disabled?: boolean;
  size?: "small" | "medium";
  expandOptionsWidth?: boolean;
  optionsBoxAlign?: "start" | "end";
  optionsTextAlign?: "flex-start" | "center" | "flex-end";
  optionsHeight?: string;
  hideOptions?: boolean;
  customStyle?: React.CSSProperties;
  onSelect?: () => void;
  isOpen?: boolean;
}

const SelectBox = (props: SelectBoxProps) => {
  const {
    options,
    width,
    height,
    defaultOptionLabel,
    selectedOptionLabel,
    disabled,
    size,
    expandOptionsWidth,
    optionsBoxAlign = "start",
    optionsTextAlign = "flex-start",
    optionsHeight,
    onSelect,
    isOpen: isOpenProp,
    hideOptions,
    customStyle,
  } = props;

  /* **********************************
   * LOCAL STATE
   * 컴포넌트 내부에서 사용하는 상태
   ********************************** */
  const [selectedOption, setSelectedOption] = useState<IOption | null>(() => {
    if (selectedOptionLabel) {
      return options.find((option) => option.label === selectedOptionLabel);
    }
    return null;
  });

  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [hoveredIndex, setHoveredIndex] = useState<number>(-1);
  const mergedIsOpen = isOpenProp ?? isOpen;

  /* **********************************
   * USE EFFECT
   * 컴포넌트 렌더링 시 실행되는 함수
   ********************************** */
  // 선택된 옵션 렌더링
  useEffect(() => {
    const found = options.find(
      (option) => option.label === selectedOptionLabel
    );
    setSelectedOption(found || null);
  }, [selectedOptionLabel, options]);

  // 비활성화 상태일 경우 초기화
  useEffect(() => {
    if (disabled) {
      setIsOpen(false);
      setSelectedOption(null);
    }
  }, [disabled]);

  /* **********************************
   * HOOKS
   ********************************** */
  useClickOutsideCloseModal([dropdownRef], () => {
    setIsOpen(false);
  });

  /* **********************************
   * COMPONENT
   * 컴포넌트 정의
   ********************************** */
  return (
    <div
      ref={dropdownRef}
      style={{ position: "relative", width: width || "100%" }}
    >
      <SelectedOptionBox
        onClick={() => {
          if (onSelect) onSelect();
          if (!disabled) setIsOpen((prev) => !prev);
        }}
        $isOpen={mergedIsOpen}
        $isComplete={(!mergedIsOpen && selectedOption !== null) || disabled}
        height={height}
        size={size}
        disabled={disabled}
        style={customStyle}
      >
        {selectedOption?.label || defaultOptionLabel || "선택"}
        {mergedIsOpen ? (
          <ChevronUp
            color={customStyle?.color ?? colors.blue600}
            style={{ marginLeft: 5 }}
          />
        ) : (
          <ChevronDown
            color={
              customStyle?.color ??
              (selectedOption ? colors.gray600 : colors.gray200)
            }
            style={{ marginLeft: 5 }}
          />
        )}
      </SelectedOptionBox>
      {!disabled && mergedIsOpen && !hideOptions && (
        <DropdownContent
          $expandOptionsWidth={expandOptionsWidth}
          $optionHeight={optionsHeight}
          $optionsBoxAlign={optionsBoxAlign}
          $optionsTextAlign={optionsTextAlign}
        >
          {options.map((option, index) => (
            <div
              key={index}
              onClick={() => {
                option.onSelect();
                setIsOpen(false);
              }}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(-1)}
              style={{
                backgroundColor:
                  hoveredIndex === index ? colors.blue50 : colors.white,
                borderTopLeftRadius: index === 0 ? 8 : 0,
                borderTopRightRadius: index === 0 ? 8 : 0,
                borderBottomLeftRadius: index === options.length - 1 ? 8 : 0,
                borderBottomRightRadius: index === options.length - 1 ? 8 : 0,
                paddingLeft: 16,
              }}
            >
              <span
                style={{
                  paddingLeft: 0,
                  color:
                    hoveredIndex === index ? colors.gray900 : colors.gray500,
                  fontSize:
                    size === "small"
                      ? fonts.label4Medium.fontSize
                      : fonts.body3Medium.fontSize,
                  fontFamily:
                    size === "small"
                      ? fonts.label4Medium.fontFamily
                      : fonts.body3Medium.fontFamily,
                  lineHeight:
                    size === "small"
                      ? fonts.label4Medium.lineHeight
                      : fonts.body3Medium.lineHeight,
                }}
              >
                {option.label}
              </span>
            </div>
          ))}
        </DropdownContent>
      )}
    </div>
  );
};

export default SelectBox;

/* **********************************
 * STYLED COMPONENT
 ********************************** */
const SelectedOptionBox = styled.div<{
  width?: string;
  height?: string;
  size?: "small" | "medium";
  $isOpen: boolean;
  $isComplete: boolean;
  disabled: boolean;
}>`
  cursor: ${(props) => (props.disabled ? "default" : "pointer")};
  border-radius: 8px;
  width: ${(props) => props.width || "100%"};
  height: ${(props) => props.height || "48px"};
  padding-left: 16px;
  padding-right: 16px;
  font-size: ${(props) =>
    props.size === "small"
      ? fonts.label4Medium.fontSize
      : fonts.label3Medium.fontSize};
  font-family: ${(props) =>
    props.size === "small"
      ? fonts.label4Medium.fontFamily
      : fonts.label3Medium.fontFamily};
  line-height: ${(props) =>
    props.size === "small"
      ? fonts.label4Medium.lineHeight
      : fonts.label3Medium.lineHeight};
  color: ${(props) =>
    props.$isOpen
      ? colors.gray900
      : props.$isComplete
      ? colors.gray900
      : colors.gray500};
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: ${(props) =>
    props.$isComplete && !props.disabled ? colors.gray50 : colors.white};
  border: ${(props) =>
    props.$isOpen
      ? `1px solid ${colors.blue600}`
      : props.$isComplete && !props.disabled
      ? `none`
      : `1px solid ${colors.gray100}`};
`;

const DropdownContent = styled.div<{
  $expandOptionsWidth?: boolean;
  $optionHeight?: string;
  $optionsBoxAlign: "start" | "end";
  $optionsTextAlign: "flex-start" | "center" | "flex-end";
}>`
  position: absolute;
  background-color: ${colors.white};
  border: 1px solid ${colors.gray100};
  border-radius: 8px;
  z-index: 1000;
  width: 100%;
  top: 52px;

  & > div {
    cursor: pointer;
    display: flex;
    flex-direction: row;
    justify-content: ${({ $optionsTextAlign }) => $optionsTextAlign};
    align-items: center;
    height: ${({ $optionHeight }) => ($optionHeight ? $optionHeight : "44px")};
    padding-left: 10px;
    color: ${colors.gray500};
    background-color: ${colors.white};
    font-size: ${fonts.body3Regular.fontSize};
    font-family: ${fonts.body3Regular.fontFamily};
    line-height: ${fonts.body3Regular.lineHeight};

    ${({ $optionsTextAlign }) =>
      $optionsTextAlign &&
      css`
        padding-left: 0px;
        > span {
          padding-left: 0px !important;
        }
      `}
  }
  border-radius: 8px;
  top: 52px;

  ${({ $expandOptionsWidth, $optionsBoxAlign, $optionsTextAlign }) =>
    $expandOptionsWidth &&
    css`
      ${$optionsBoxAlign === "start" ? "left: 0;" : "right: 0;"};
      width: fit-content;
      span {
        white-space: nowrap;
        padding-right: 10px;
      }
    `}
`;
